import PropTypes from "prop-types";
import { Gauge } from "./ui/gauge";
import { StatusDot } from "./StatusDot";

const finite = (value) => typeof value === "number" && Number.isFinite(value);
// Gauge PnL and clean-performance telemetry use percentage units at the data
// boundary. Never infer fractional ratio units from magnitude here.
const rawPercent = (value) => finite(value) ? value : null;
const boundedPercentMagnitude = (value) => finite(value) ? Math.min(100, Math.abs(value)) : null;
const formatUsd = (value) => finite(value) ? `$${value.toLocaleString(undefined, value >= 1000 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Unavailable";
const formatPercent = (value) => {
  const normalized = rawPercent(value);
  if (normalized === null) return "Unavailable";
  return `${normalized > 0 ? "+" : ""}${normalized.toFixed(Math.abs(normalized) < 10 ? 2 : 1)}%`;
};
const confidencePercent = (value) => finite(value) ? Math.max(0, Math.min(100, value <= 1 ? value * 100 : value)) : null;

export default function CoinGauge({
  symbol = "", price = null, updatedAt = null, freshness = "unknown", mode = "PAPER",
  signalDirection = null, confidence = null, pnl = null, pnlKind = "unavailable",
  positionState = "loading", positionQty = null, positionUnavailableReason = null,
  performancePct = null, performanceEligible = false,
}) {
  const sym = String(symbol || "").toUpperCase();
  const confidenceValue = confidencePercent(confidence);
  const pnlValue = rawPercent(pnl);
  // Operator-visible clean performance keeps the raw percentage unit. Only
  // the SVG ring receives a bounded magnitude.
  const cleanPerformanceValue = performanceEligible ? rawPercent(performancePct) : null;
  const overlayValue = boundedPercentMagnitude(cleanPerformanceValue);
  const overlayColor = overlayValue === null ? "gray" : cleanPerformanceValue > 0 ? "green" : cleanPerformanceValue < 0 ? "red" : "gray";
  const positionText = positionState === "open" ? `OPEN${finite(positionQty) ? ` · Qty ${positionQty}` : ""}` : positionState === "flat" ? "FLAT / NO POSITION" : positionState === "unavailable" ? "LIVE POSITION UNAVAILABLE" : "POSITION LOADING";
  const pnlVariant = pnlValue === null ? "neutral" : pnlValue > 0 ? "positive" : pnlValue < 0 ? "negative" : "neutral";
  const directionText = signalDirection === null || signalDirection === undefined || signalDirection === "" ? "Unavailable" : String(signalDirection).toUpperCase();
  const confidenceText = confidenceValue === null ? "Unavailable" : `${confidenceValue.toFixed(confidenceValue < 10 ? 1 : 0)}%`;
  const performanceText = cleanPerformanceValue === null ? "No eligible clean sample" : `${formatPercent(cleanPerformanceValue)} clean performance`;

  return (
    <div className="coin-card" data-gauge-state={freshness === "offline" ? "offline" : "default"} data-testid={`coin-gauge-${sym}-${mode}`}>
      <div className="gauge-wrap">
        <Gauge value={100} label="" subtitle="" center="" color="cyan" overlayValue={overlayValue} overlayColor={overlayColor} overlayLabel={performanceText} />
        <div className="gauge-center"><span className="coin-name">{sym}</span><span className="text-[0.6rem] text-cyan-100">{mode}</span></div>
        <StatusDot status={freshness} updatedAt={updatedAt} />
      </div>
      <div className="price-row"><span className="price-text" aria-label={`USD price for ${sym}`}>{formatUsd(price)}</span></div>
      <div className="coin-status-line" data-testid="coin-gauge-status" title="Current signal direction and confidence; unavailable is distinct from zero.">{directionText} · confidence {confidenceText}</div>
      <div className="coin-secondary-grid">
        <span className="position-badge" data-testid="coin-gauge-position" title={positionUnavailableReason || "Open-position state for the selected runtime mode."}>{positionText}</span>
        <span className={`pnl-badge pnl-${pnlVariant}`} data-testid="coin-gauge-pnl" title={pnlKind === "unavailable" ? "Unrealized PnL unavailable." : `${pnlKind.replace(/_/g, " ")} PnL.`}>PnL {formatPercent(pnl)}</span>
        <span className={`performance-badge performance-${overlayColor}`} data-testid="coin-gauge-performance" title="Inner ring is clean post-epoch eligible performance only; it never uses historical context.">{performanceText}</span>
      </div>
    </div>
  );
}

CoinGauge.propTypes = {
  symbol: PropTypes.any, price: PropTypes.number, updatedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), freshness: PropTypes.string, mode: PropTypes.oneOf(["PAPER", "LIVE"]),
  signalDirection: PropTypes.string, confidence: PropTypes.number, pnl: PropTypes.number, pnlKind: PropTypes.string,
  positionState: PropTypes.oneOf(["open", "flat", "unavailable", "loading"]), positionQty: PropTypes.number, positionUnavailableReason: PropTypes.string,
  performancePct: PropTypes.number, performanceEligible: PropTypes.bool,
};
